import React from 'react';
import { Rect, Circle, Text, Line, Arc, Path } from 'react-konva';

export interface KonvaShapeRendererProps {
  shapeType: string;
  size: number;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  dash?: number[];
  dashEnabled?: boolean;
  isDeceased?: boolean;
  isStillbirth?: boolean;
  isMale?: boolean;
  isFemale?: boolean;
  isTransgender?: boolean;
  isIndexPerson?: boolean;
  isPregnancy?: boolean;
  sexualOrientation?: string;
}

/**
 * Shared Konva shape renderer that matches PersonNode.tsx exactly
 * This ensures cursor preview matches actual rendered shapes
 */
export const KonvaShapeRenderer: React.FC<KonvaShapeRendererProps> = ({
  shapeType,
  size,
  fillColor = '#f5f5f5',
  strokeColor = '#333',
  strokeWidth = 2,
  dash = [],
  dashEnabled = false,
  isDeceased = false,
  isStillbirth = false,
  isMale = false,
  isFemale = false,
  isTransgender = false,
  isIndexPerson = false,
  isPregnancy = false,
  sexualOrientation
}) => {
  // Pregnancy - triangle
  if (isPregnancy) {
    return (
      <Line
        points={[
          size/2, 0,
          size, size,
          0, size
        ]}
        closed
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    );
  }

  if (shapeType === 'culture-male') {
    const scaleFactor = 0.72;
    const sx = (size / 56) * scaleFactor;
    const sy = (size / 94) * scaleFactor;
    const rectSide = 54 * sx;
    const centerX = size / 2;
    const rectX = centerX - rectSide / 2;
    const rectY = 39 * sy;
    const stemX = centerX;
    const c = 28;
    const w = 0.9;
    const nx = (x: number) => centerX + ((c + (x - c) * w) * sx - 28 * sx);
    const imPath1 = `M ${nx(10.2224)} ${4.20515 * sy} C ${nx(9.20086)} ${12.064 * sy}, ${nx(14.9995)} ${23.187 * sy}, ${nx(27.0779)} ${20.7087 * sy} C ${nx(29.2112)} ${20.2711 * sy}, ${nx(33.1267)} ${17.7128 * sy}, ${nx(34.3581)} ${11.0182 * sy} C ${nx(34.8771)} ${8.19657 * sy}, ${nx(35.7481)} ${5.026 * sy}, ${nx(38.5067)} ${4.23786 * sy} C ${nx(41.2722)} ${3.44775 * sy}, ${nx(44.6243)} ${4.40579 * sy}, ${nx(46.9977)} ${10.4922 * sy}`;
    return (
      <>
        <Path data={`M ${stemX} ${40 * sy} V 0`} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" />
        <Rect x={rectX} y={rectY} width={rectSide} height={rectSide} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} dash={dash} dashEnabled={dashEnabled} />
        <Path data={imPath1} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" strokeLinejoin="round" />
      </>
    );
  }

  if (shapeType === 'immigration-male') {
    const scaleFactor = 0.72;
    const sx = (size / 56) * scaleFactor;
    const sy = (size / 94) * scaleFactor;
    const rectSide = 54 * sx;
    const centerX = size / 2;
    const rectX = centerX - rectSide / 2;
    const rectY = 39 * sy;
    const stemX = centerX;
    const c = 28;
    const w = 0.9;
    const nx = (x: number) => centerX + ((c + (x - c) * w) * sx - 28 * sx);
    const imPath1 = `M ${nx(10.2224)} ${4.20515 * sy} C ${nx(9.20086)} ${12.064 * sy}, ${nx(14.9995)} ${23.187 * sy}, ${nx(27.0779)} ${20.7087 * sy} C ${nx(29.2112)} ${20.2711 * sy}, ${nx(33.1267)} ${17.7128 * sy}, ${nx(34.3581)} ${11.0182 * sy} C ${nx(34.8771)} ${8.19657 * sy}, ${nx(35.7481)} ${5.026 * sy}, ${nx(38.5067)} ${4.23786 * sy} C ${nx(41.2722)} ${3.44775 * sy}, ${nx(44.6243)} ${4.40579 * sy}, ${nx(46.9977)} ${10.4922 * sy}`;
    const imPath2 = `M ${nx(5.99563)} ${10 * sy} C ${nx(4.86028)} ${17.8306 * sy}, ${nx(13.576)} ${29.9694 * sy}, ${nx(27)} ${27.5 * sy} C ${nx(29.3155)} ${27.0741 * sy}, ${nx(34.8406)} ${25.6946 * sy}, ${nx(36.7972)} ${19.7586 * sy} C ${nx(37.7606)} ${16.836 * sy}, ${nx(38.7969)} ${13.3396 * sy}, ${nx(41.869)} ${13.1615 * sy} C ${nx(44.5277)} ${13.0073 * sy}, ${nx(47.1898)} ${14.5562 * sy}, ${nx(47.4988)} ${19.5 * sy}`;
    return (
      <>
        <Path data={`M ${stemX} ${40 * sy} V 0`} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" />
        <Rect x={rectX} y={rectY} width={rectSide} height={rectSide} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} dash={dash} dashEnabled={dashEnabled} />
        <Path data={imPath1} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" strokeLinejoin="round" />
        <Path data={imPath2} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" strokeLinejoin="round" />
      </>
    );
  }

  if (shapeType === 'culture-female') {
    const scaleFactor = 0.72;
    const sx = (size / 56) * scaleFactor;
    const sy = (size / 94) * scaleFactor;
    const rectTop = 39 * sy;
    const circleR = 27 * sx;
    const centerX = size / 2;
    const circleCx = centerX;
    const circleCy = rectTop + circleR;
    const stemX = centerX;
    const c = 28;
    const w = 0.9;
    const nx = (x: number) => centerX + ((c + (x - c) * w) * sx - 28 * sx);
    const ifPath1 = `M ${nx(10.2224)} ${4.20515 * sy} C ${nx(9.20086)} ${12.064 * sy}, ${nx(14.9995)} ${23.187 * sy}, ${nx(27.0779)} ${20.7087 * sy} C ${nx(29.2112)} ${20.2711 * sy}, ${nx(33.1267)} ${17.7128 * sy}, ${nx(34.3581)} ${11.0182 * sy} C ${nx(34.8771)} ${8.19657 * sy}, ${nx(35.7481)} ${5.026 * sy}, ${nx(38.5067)} ${4.23786 * sy} C ${nx(41.2722)} ${3.44775 * sy}, ${nx(44.6243)} ${4.40579 * sy}, ${nx(46.9977)} ${10.4922 * sy}`;
    return (
      <>
        <Path data={`M ${stemX} ${40 * sy} V 0`} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" />
        <Circle x={circleCx} y={circleCy} radius={circleR} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} dash={dash} dashEnabled={dashEnabled} />
        <Path data={ifPath1} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" strokeLinejoin="round" />
      </>
    );
  }

  if (shapeType === 'immigration-female') {
    const scaleFactor = 0.72;
    const sx = (size / 56) * scaleFactor;
    const sy = (size / 94) * scaleFactor;
    const rectTop = 39 * sy;
    const circleR = 27 * sx;
    const centerX = size / 2;
    const circleCx = centerX;
    const circleCy = rectTop + circleR;
    const stemX = centerX;
    const c = 28;
    const w = 0.9;
    const nx = (x: number) => centerX + ((c + (x - c) * w) * sx - 28 * sx);
    const ifPath1 = `M ${nx(10.2224)} ${4.20515 * sy} C ${nx(9.20086)} ${12.064 * sy}, ${nx(14.9995)} ${23.187 * sy}, ${nx(27.0779)} ${20.7087 * sy} C ${nx(29.2112)} ${20.2711 * sy}, ${nx(33.1267)} ${17.7128 * sy}, ${nx(34.3581)} ${11.0182 * sy} C ${nx(34.8771)} ${8.19657 * sy}, ${nx(35.7481)} ${5.026 * sy}, ${nx(38.5067)} ${4.23786 * sy} C ${nx(41.2722)} ${3.44775 * sy}, ${nx(44.6243)} ${4.40579 * sy}, ${nx(46.9977)} ${10.4922 * sy}`;
    const ifPath2 = `M ${nx(5.99563)} ${10 * sy} C ${nx(4.86028)} ${17.8306 * sy}, ${nx(13.576)} ${29.9694 * sy}, ${nx(27)} ${27.5 * sy} C ${nx(29.3155)} ${27.0741 * sy}, ${nx(34.8406)} ${25.6946 * sy}, ${nx(36.7972)} ${19.7586 * sy} C ${nx(37.7606)} ${16.836 * sy}, ${nx(38.7969)} ${13.3396 * sy}, ${nx(41.869)} ${13.1615 * sy} C ${nx(44.5277)} ${13.0073 * sy}, ${nx(47.1898)} ${14.5562 * sy}, ${nx(47.4988)} ${19.5 * sy}`;
    return (
      <>
        <Path data={`M ${stemX} ${40 * sy} V 0`} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" />
        <Circle x={circleCx} y={circleCy} radius={circleR} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} dash={dash} dashEnabled={dashEnabled} />
        <Path data={ifPath1} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" strokeLinejoin="round" />
        <Path data={ifPath2} stroke={strokeColor} strokeWidth={strokeWidth} fillEnabled={false} strokeLinecap="round" strokeLinejoin="round" />
      </>
    );
  }

  // Institution - house shape
  if (shapeType === 'institution') {
    return (
      <Line
        points={[
          0, size,
          0, size/2,
          size/2, 0,
          size, size/2,
          size, size
        ]}
        closed
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    );
  }

  // Pet - diamond
  if (shapeType === 'pet') {
    return (
      <Line
        points={[
          size/2, 0,
          size, size/2,
          size/2, size,
          0, size/2
        ]}
        closed
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    );
  }

  // Transgender male - square with inner circle
  if (isMale && isTransgender) {
    return (
      <>
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          stroke={strokeColor}
          fill={fillColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Transgender female - circle with inner square
  if (isFemale && isTransgender) {
    return (
      <>
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Rect
          x={size * 0.15}
          y={size * 0.15}
          width={size * 0.7}
          height={size * 0.7}
          fill=""
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Index person - reflect underlying sex and deceased status with double border
  if (isIndexPerson) {
    if (isMale) {
      return (
        <>
          <Rect
            x={0}
            y={0}
            width={size}
            height={size}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth * 2}
            dash={dash}
            dashEnabled={dashEnabled}
          />
          <Rect
            x={size * 0.15}
            y={size * 0.15}
            width={size * 0.7}
            height={size * 0.7}
            fillEnabled={false}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
          {isDeceased && (
            <>
              <Line points={[0, 0, size, size]} stroke={strokeColor} strokeWidth={2} />
              <Line points={[0, size, size, 0]} stroke={strokeColor} strokeWidth={2} />
            </>
          )}
        </>
      );
    }
    if (isFemale) {
      return (
        <>
          <Circle
            x={size/2}
            y={size/2}
            radius={size/2}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth * 2}
            dash={dash}
            dashEnabled={dashEnabled}
          />
          <Circle
            x={size/2}
            y={size/2}
            radius={size * 0.35}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
          {isDeceased && (
            <>
              <Line points={[0, 0, size, size]} stroke={strokeColor} strokeWidth={2} />
              <Line points={[0, size, size, 0]} stroke={strokeColor} strokeWidth={2} />
            </>
          )}
        </>
      );
    }

    // Unknown sex - diamond with double border
    return (
      <>
        <Line
          points={[size/2, 0, size, size/2, size/2, size, 0, size/2]}
          closed
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth * 2}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[size/2, size * 0.15625, size * 0.84375, size/2, size/2, size * 0.84375, size * 0.15625, size/2]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        {isDeceased && (
          <>
            <Line points={[size*0.1, size*0.1, size*0.9, size*0.9]} stroke={strokeColor} strokeWidth={2} />
            <Line points={[size*0.9, size*0.1, size*0.1, size*0.9]} stroke={strokeColor} strokeWidth={2} />
          </>
        )}
      </>
    );
  }

  // Gay - square with triangle indicator
  if (isMale && sexualOrientation === 'gay') {
    return (
      <>
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            size * 0.1, size * 0.1,       // top-left corner
            size * 0.9, size * 0.1,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Lesbian - circle with triangle indicator
  if (isFemale && sexualOrientation === 'lesbian') {
    return (
      <>
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.2, size * 0.2,       // top-left corner
            size * 0.8, size * 0.2,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Bisexual male - square with dashed arch
  if (isMale && sexualOrientation === 'bisexual') {
    return (
      <>
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.1, size * 0.1,       // top-left corner
            size * 0.9, size * 0.1,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={[6, 3]}           // 6‑px stroke, 4‑px gap
          dashEnabled
        />
      </>
    );
  }

  // Bisexual female - circle with dashed arch
  if (isFemale && sexualOrientation === 'bisexual') {
    return (
      <>
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.2, size * 0.2,       // top-left corner
            size * 0.8, size * 0.2,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={[6, 3]} 
          dashEnabled={true}
        />
      </>
    );
  }

  // Handle complex gender identity shapes
  if (shapeType === 'agender') {
    const radius = size * 0.35;
    return (
      <>
        <Circle
          x={size / 2}
          y={size / 2}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[size / 2, size / 2 - radius - size * 0.3, size / 2, size / 2 - radius * 1]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[size / 2 - radius, size / 2, size / 2 + radius, size / 2]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'agender-classic') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center + radius * 0.3}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius * 1.5,
            center,
            center - radius * 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'two-spirit') {
    return (
      <>
        <Circle
          x={size / 2}
          y={size * 0.3}
          radius={size * 0.22}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Circle
          x={size / 2}
          y={size * 0.75}
          radius={size * 0.22}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[-size * 0, size * 0.76, size * 0.3, size * 0.76]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[size * 0.7, size * 0.3, size * 0.95, size * 0.3]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'epicene') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Circle
          x={center}
          y={center}
          radius={radius * 0.25}
          fill={strokeColor}
          strokeEnabled={false}
        />
        <Line
          points={[
            center,
            center - radius,
            center,
            center + radius
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.5,
            center,
            center - radius * 1,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.5,
            center,
            center + radius * 1,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Arc
          x={center + radius * 0}
          y={center}
          innerRadius={radius * 1.5}
          outerRadius={radius * 1.5}
          angle={-270}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Arc
          x={center}
          y={center}
          innerRadius={radius * 1.5}
          outerRadius={radius * 1.5}
          angle={-270}
          rotation={180}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'epicene-classic') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Circle
          x={center}
          y={center}
          radius={radius * 0.35}
          fill={strokeColor}
          strokeEnabled={false}
        />
        <Line
          points={[
            center + radius,
            center - radius * 0.2,
            center + radius * 1.3,
            center - radius * 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center + radius * 0.2,
            center - radius * 1.3,
            center + radius * 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'gender-fluid') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.70,
            center - radius * 0.70,
            center - radius * 1.38,
            center - radius * 1.38
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1 - radius * 0.3,
            center + radius * 1 - radius * 0.3,
            center + radius * 1 + radius * 0.3,
            center + radius * 1 + radius * 0.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1 - radius * 0.3,
            center + radius * 1 + radius * 0.3,
            center + radius * 1 + radius * 0.3,
            center + radius * 1 - radius * 0.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius,
            center - radius * 0.2,
            center + radius * 1.3,
            center - radius * 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center + radius * 0.2,
            center - radius * 1.3,
            center + radius * 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'gender-fluid-classic') {
    const radius = size * 0.35;
    const center = size / 2;
    const circleRadius = radius * 0.75;
    const circleCenterY = center - radius * 0.4;
    const arrowTipX = center + radius * 0.1;
    const arrowTipY = center + radius * 1.24;
    const arrowWing = radius * 0.3;
    return (
      <>
        <Circle
          x={center}
          y={circleCenterY}
          radius={circleRadius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Path
          data={`
            M ${center + radius * 0.24} ${center + radius * 0.72}
            C ${center + radius * 0.54} ${center + radius * 0.92},
              ${center + radius * 0.36} ${center + radius * 1.15},
              ${center + radius * 0.12} ${center + radius * 1.18}
          `}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
        <Path
          data={`
            M ${center} ${center + radius * 0.35}
            C ${center - radius * 0.24} ${center + radius * 0.48},
              ${center - radius * 0.06} ${center + radius * 0.78},
              ${center + radius * 0.24} ${center + radius * 0.74}
          `}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
        <Line
          points={[
            arrowTipX,
            arrowTipY,
            arrowTipX + arrowWing,
            arrowTipY
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            arrowTipX,
            arrowTipY,
            arrowTipX,
            arrowTipY - arrowWing
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'bigender') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'trigender') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35,
            center - radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.2,
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'trigender-equal') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center,
            center + radius,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius,
            center,
            center + radius
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35,
            center - radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.2,
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'pangender') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center,
            center + radius,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius,
            center,
            center + radius * 1.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.35,
            center + radius * 1.35,
            center,
            center + radius * 1.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.35,
            center + radius * 1.35,
            center,
            center + radius * 1.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.3,
            center + radius * 1.2,
            center + radius * 0.3,
            center + radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center, center - radius,
            center, center - radius * 1.8
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'xenogender') {
    const radius = size * 0.35;
    const center = size / 2;
    const arcWidth = radius * 0.6;
    const arcDepth = radius * 0.35;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Path
          data={`
            M ${center - arcWidth} ${center + radius * 1.5}
            C ${center - arcWidth * 0.5} ${center + radius * 1.5 - arcDepth},
              ${center - arcWidth * 0.15} ${center + radius * 1.5 - arcDepth},
              ${center} ${center + radius * 1.5}
          `}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
        <Path
          data={`
            M ${center} ${center + radius * 1.5}
            C ${center + arcWidth * 0.15} ${center + radius * 1.5 - arcDepth},
              ${center + arcWidth * 0.5} ${center + radius * 1.5 - arcDepth},
              ${center + arcWidth} ${center + radius * 1.5}
          `}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
      </>
    );
  }

  if (shapeType === 'travesti') {
    const radius = size * 0.35;
    const center = size / 2;
    const circleCenterX = center + radius * 0.2;
    const circleCenterY = center + radius * 0.15;
    const barBaseX = circleCenterX - radius * 0.8;
    const barBaseY = circleCenterY - radius * 0.6;
    const barTopX = barBaseX - radius * 0.6;
    const barTopY = barBaseY - radius * 0.6;
    const barCapLength = radius * 0.6;
    return (
      <>
        <Circle
          x={circleCenterX}
          y={circleCenterY}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            circleCenterX - radius * 0.7,
            circleCenterY - radius * 0.6,
            barTopX,
            barTopY
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            barTopX - barCapLength * 0.6,
            barTopY + barCapLength * 0.6,
            barTopX + barCapLength * 0.5,
            barTopY - barCapLength * 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'third-gender') {
    const radius = size * 0.35;
    const center = size / 2;
    const circleCenterX = center + radius * 0.5;
    const circleCenterY = center;
    const circleEdgeX = circleCenterX - radius;
    const circleEdgeY = circleCenterY;
    const stemLength = radius * 0.4;
    const stemEndX = circleEdgeX - stemLength;
    const wingLength = radius * 0.55;
    return (
      <>
        <Circle
          x={circleCenterX}
          y={circleCenterY}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.5,
            center,
            center - radius * 0.9,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            stemEndX,
            circleEdgeY,
            stemEndX - wingLength,
            circleEdgeY - wingLength * 0.6,
            stemEndX - wingLength,
            circleEdgeY + wingLength * 0.6
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'hijra') {
    const radius = size * 0.35;
    const center = size / 2;
    const circleEdgeX = center - radius * 1.1;
    const circleEdgeY = center;
    const stemLength = radius * 0.35;
    const stemEndX = circleEdgeX - stemLength;
    const wingLength = radius * 0.5;
    
    const outerR = radius * 1.1;
    const innerR = radius * 0.9;
    const offset = radius * 0.2;
    return (
      <>
        <Line
          points={[
            circleEdgeX,
            circleEdgeY,
            stemEndX,
            circleEdgeY
          ]}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            stemEndX,
            circleEdgeY,
            stemEndX - wingLength,
            circleEdgeY - wingLength * 0.6,
            stemEndX - wingLength,
            circleEdgeY + wingLength * 0.6
          ]}
          closed
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Path
          data={`
            M ${center + outerR} ${center}
            A ${outerR} ${outerR} 0 1 0 ${center - outerR} ${center}
            A ${outerR} ${outerR} 0 1 0 ${center + outerR} ${center}
            M ${center + offset + innerR} ${center}
            A ${innerR} ${innerR} 0 1 1 ${center + offset - innerR} ${center}
            A ${innerR} ${innerR} 0 1 1 ${center + offset + innerR} ${center}
            Z
          `}
          fill={strokeColor}
          strokeEnabled={false}
        />
      </>
    );
  }

  if (shapeType === 'neurogender') {
    const radius = size * 0.35
    const center = size / 2;
    return (
      <>
        <Line
          points={[
            center, center - radius,
            center + radius, center,
            center, center + radius,
            center - radius, center
          ]}
          closed
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'detragender') {
    return (
      <>
        <Circle
          x={size / 2}
          y={size / 2}
          radius={size * 0.48}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Circle
          x={size / 2}
          y={size / 2 - size * 0.48 * 0.45}
          radius={size * 0.48 * 0.56}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            size / 2, size / 2 + size * 0.60 * 0.1,
            size / 2 + size * 0.60 * 0.35, size / 2 + size * 0.60 * 0.45,
            size / 2, size / 2 + size * 0.60 * 0.8,
            size / 2 - size * 0.60 * 0.35, size / 2 + size * 0.60 * 0.45
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'centrgender') {
    const radius = size * 0.35;
    const center = size / 2;
    const arrowHead = radius * 0.7;
    const rightArrowLen = radius * 0.2;
    const rightWingOffset = radius * 0.3;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius * 1.5,
            center,
            center + radius * 1.5
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius,
            center,
            center - radius - arrowHead
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.35,
            center - radius - arrowHead * 0.5,
            center,
            center - radius - arrowHead
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.35,
            center - radius - arrowHead * 0.5,
            center,
            center - radius - arrowHead
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius,
            center,
            center + radius + arrowHead
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.35,
            center + radius + arrowHead * 0.5,
            center,
            center + radius + arrowHead
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.35,
            center + radius + arrowHead * 0.5,
            center,
            center + radius + arrowHead
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius,
            center - rightArrowLen,
            center + radius + rightWingOffset,
            center - rightArrowLen + rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius,
            center - rightArrowLen,
            center + radius - rightWingOffset,
            center - rightArrowLen + rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center + rightArrowLen,
            center - radius - rightWingOffset,
            center + rightArrowLen - rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center + rightArrowLen,
            center - radius + rightWingOffset,
            center + rightArrowLen - rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.3, center - radius * 1.2,
            center + radius * 0.3, center - radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.3, center + radius * 1.2,
            center + radius * 0.3, center + radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'isogender') {
    const radius = size * 0.35;
    const center = size / 2;
    const arrowHead = radius * 0.5;
    const rightArrowLen = radius * 0.2;
    const rightWingOffset = radius * 0.3;
    const topCrossArm = radius * 0.35;
    const topStemEndY = center - radius - arrowHead * 0.8;
    const topStemEndX = center + radius + arrowHead * 0.8;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius * 1.5,
            center,
            center + radius * 1.5
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - topCrossArm * 0.6,
            topStemEndY - topCrossArm * 0.6,
            center + topCrossArm * 0.6,
            topStemEndY + topCrossArm * 0.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - topCrossArm * 0.6,
            topStemEndY + topCrossArm * 0.6,
            center + topCrossArm * 0.6,
            topStemEndY - topCrossArm * 0.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - topCrossArm * 0.6,
            topStemEndX - topCrossArm * 0.6,
            center + topCrossArm * 0.6,
            topStemEndX + topCrossArm * 0.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - topCrossArm * 0.6,
            topStemEndX + topCrossArm * 0.6,
            center + topCrossArm * 0.6,
            topStemEndX - topCrossArm * 0.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius,
            center - rightArrowLen,
            center + radius + rightWingOffset,
            center - rightArrowLen + rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius,
            center - rightArrowLen,
            center + radius - rightWingOffset,
            center - rightArrowLen + rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center + rightArrowLen,
            center - radius - rightWingOffset,
            center + rightArrowLen - rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center + rightArrowLen,
            center - radius + rightWingOffset,
            center + rightArrowLen - rightWingOffset
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'ultergender') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.75,
            center - radius * 0.65,
            center + radius * 1.5,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.5 - radius * 0.4 * Math.SQRT1_2,
            center - radius * 1.4 - radius * 0.4 * Math.SQRT1_2,
            center + radius * 1.5 + radius * 0.4 * Math.SQRT1_2,
            center - radius * 1.4 + radius * 0.4 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius,
            center,
            center + radius * 1.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.35,
            center + radius * 1.2,
            center,
            center + radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.35,
            center + radius * 1.2,
            center,
            center + radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'ultergender-equal') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center,
            center + radius,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius,
            center,
            center + radius
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.70,
            center - radius * 0.70,
            center - radius * 1.38,
            center - radius * 1.38
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.95 - radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 - radius * 0.25
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'male-equal') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center,
            center + radius,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius,
            center,
            center + radius
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'questioning') {
    const radius = size * 0.35;
    const center = size / 2;
    const arcRotationDeg = 230;
    const arcStartAngleRad = (arcRotationDeg * Math.PI) / 180;
    const arrowArmSpread = Math.PI / 4;
    const arrowArmLength = radius * 0.35;
    const arcStartX = center + radius * Math.cos(arcStartAngleRad);
    const arcStartY = center + radius * Math.sin(arcStartAngleRad);
    const arrowBaseAngle = arcStartAngleRad + Math.PI / 2;
    return (
      <>
        <Rect
          x={center - radius * 0.7}
          y={center - radius * 1.2}
          width={radius * 1.8}
          height={radius * 2.8}
          strokeWidth={strokeWidth}
        />
        <Arc
          x={center + radius * 0}
          y={center}
          innerRadius={radius * 1}
          outerRadius={radius * 1}
          angle={-140}
          rotation={230}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            arcStartX,
            arcStartY,
            arcStartX + arrowArmLength * Math.cos(arrowBaseAngle + arrowArmSpread),
            arcStartY + arrowArmLength * Math.sin(arrowBaseAngle + arrowArmSpread)
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            arcStartX,
            arcStartY,
            arcStartX + arrowArmLength * Math.cos(arrowBaseAngle - arrowArmSpread),
            arcStartY + arrowArmLength * Math.sin(arrowBaseAngle - arrowArmSpread)
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'queer') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Rect
          x={center - radius * 1.3}
          y={center - radius * 1.2}
          width={radius * 2.2}
          height={radius * 2.8}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.8,
            center - radius,
            center + radius * 0.4,
            center + radius
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.8,
            center - radius * 1,
            center - radius * 1.3,
            center - radius * 0.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.4,
            center + radius * 1,
            center + radius * 0.9,
            center + radius * 0.65
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.2,
            center,
            center - radius * 1,
            center + radius * 1
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'polyamorous') {
    const radius = size * 0.35;
    const center = size / 2;
    const glyphRadius = radius * 1.2;
    const heartPath = `
      M ${center} ${center + glyphRadius * 0.04}
      C ${center + glyphRadius * 0.85} ${center - glyphRadius * 0.48}, ${center + glyphRadius * 1.2} ${center + glyphRadius * 0.58}, ${center} ${center + glyphRadius * 1.12}
      C ${center - glyphRadius * 1.2} ${center + glyphRadius * 0.58}, ${center - glyphRadius * 0.85} ${center - glyphRadius * 0.48}, ${center} ${center + glyphRadius * 0.04}
      Z
    `;
  
    const infinityCenterY = center + glyphRadius * 0.35;
    const infinityWidth = glyphRadius;
    const infinityHeight = glyphRadius * 0.55;
    const loopGap = glyphRadius * 0.14;
    const loopReach = loopGap + infinityWidth;
    const centerCtrlOffset = loopGap + infinityWidth * 0.42;
    const infinityPath = `
      M ${center} ${infinityCenterY}
      C ${center - centerCtrlOffset} ${infinityCenterY - infinityHeight},
        ${center - loopReach} ${infinityCenterY - infinityHeight * 0.4},
        ${center - loopReach} ${infinityCenterY}
      C ${center - loopReach} ${infinityCenterY + infinityHeight * 0.4},
        ${center - centerCtrlOffset} ${infinityCenterY + infinityHeight},
        ${center} ${infinityCenterY}
      C ${center + centerCtrlOffset} ${infinityCenterY - infinityHeight},
        ${center + loopReach} ${infinityCenterY - infinityHeight * 0.4},
        ${center + loopReach} ${infinityCenterY}
      C ${center + loopReach} ${infinityCenterY + infinityHeight * 0.4},
        ${center + centerCtrlOffset} ${infinityCenterY + infinityHeight},
        ${center} ${infinityCenterY}
    `;
  
    return (
      <>
        <Path
          data={heartPath}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill={fillColor}
        />
        <Path
          data={infinityPath}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
      </>
    );
  }

  if (shapeType === 'self-partnering') {
    const radius = size * 0.35;
    const center = size / 2;
    const glyphRadius = radius * 1.2;

    const heartPath = `
      M ${center} ${center + glyphRadius * 0.05}
      C ${center + glyphRadius * 0.85} ${center - glyphRadius * 0.45}, ${center + glyphRadius * 1.2} ${center + glyphRadius * 0.55}, ${center} ${center + glyphRadius * 1.08}
      C ${center - glyphRadius * 1.2} ${center + glyphRadius * 0.55}, ${center - glyphRadius * 0.85} ${center - glyphRadius * 0.45}, ${center} ${center + glyphRadius * 0.05}
      Z
    `;

    const infinityCenterY = center + glyphRadius * 0.4;
    const infinityWidth = glyphRadius * 0.9;
    const infinityHeight = glyphRadius * 0.5;
    const loopGap = glyphRadius * 0.12;
    const loopReach = loopGap + infinityWidth;
    const centerCtrlOffset = loopGap + infinityWidth * 0.4;
    const infinityPath = `
      M ${center} ${infinityCenterY}
      C ${center - centerCtrlOffset} ${infinityCenterY - infinityHeight},
        ${center - loopReach} ${infinityCenterY - infinityHeight * 0.4},
        ${center - loopReach} ${infinityCenterY}
      C ${center - loopReach} ${infinityCenterY + infinityHeight * 0.4},
        ${center - centerCtrlOffset} ${infinityCenterY + infinityHeight},
        ${center} ${infinityCenterY}
      C ${center + centerCtrlOffset} ${infinityCenterY - infinityHeight},
        ${center + loopReach} ${infinityCenterY - infinityHeight * 0.4},
        ${center + loopReach} ${infinityCenterY}
      C ${center + loopReach} ${infinityCenterY + infinityHeight * 0.4},
        ${center + centerCtrlOffset} ${infinityCenterY + infinityHeight},
        ${center} ${infinityCenterY}
    `;

    return (
      <>
        <Path
          data={heartPath}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill={fillColor}
        />
        <Path
          data={infinityPath}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
        <Line
          points={[
            center - glyphRadius,
            center + glyphRadius * 0.3,
            center - glyphRadius - glyphRadius * 0.2,
            center + glyphRadius * 0.3 + glyphRadius * 0.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - glyphRadius,
            center + glyphRadius * 0.3,
            center - glyphRadius + glyphRadius * 0.2,
            center + glyphRadius * 0.3 + glyphRadius * 0.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + glyphRadius,
            center + glyphRadius * 0.55,
            center + glyphRadius - glyphRadius * 0.2,
            center + glyphRadius * 0.55 - glyphRadius * 0.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + glyphRadius,
            center + glyphRadius * 0.55,
            center + glyphRadius + glyphRadius * 0.2,
            center + glyphRadius * 0.55 - glyphRadius * 0.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'nonamorous') {
    const radius = size * 0.35;
    const center = size / 2;
    const arcPath = `
      M ${center - radius * 1.1} ${center - radius * 0.2}
      C ${center - radius * 0.75} ${center - radius * 2.1},
        ${center - radius * 0.45} ${center + radius * 0.95},
        ${center} ${center + radius * 1.1}
      C ${center + radius * 0.45} ${center + radius * 1.0},
        ${center + radius * 0.8} ${center - radius * 0.1},
        ${center + radius * 0.9} ${center - radius * 0.95}
    `;
    return (
      <>
        <Rect
          x={center - radius * 1.1}
          y={center - radius * 1}
          width={radius * 2.1}
          height={radius * 2.1}
          strokeWidth={strokeWidth}
        />
        <Path
          data={arcPath}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'ambiamorous') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Text
          x={center - radius * 1}
          y={center - radius * 1.3}
          text="α"
          fontStyle="normal"
          fontSize={radius * 3}
          fill={strokeColor}
        />
      </>
    );
  }

  if (shapeType === 'polyflexible') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Text
          x={center + radius * 0.75}
          y={center - radius * 0.95}
          text="µ"
          fontSize={radius * 0.9}
          fill={strokeColor}
        />
        <Text
          x={center - radius * 1.5}
          y={center - radius * 1}
          text="π"
          fontStyle="normal"
          fontSize={radius * 3}
          fill={strokeColor}
        />
      </>
    );
  }

  if (shapeType === 'monoflexible') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Text
          x={center + radius * 0.5}
          y={center - radius * 1}
          text="π"
          fontSize={radius * 0.9}
          fill={strokeColor}
        />
        <Text
          x={center - radius * 1}
          y={center - radius * 1}
          text="µ"
          fontStyle="normal"
          fontSize={radius * 2.5}
          fill={strokeColor}
        />
      </>
    );
  }

  if (shapeType === 'female-equal') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius,
            center,
            center + radius,
            center
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center - radius,
            center,
            center + radius
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'intersex') {
    const radius = size * 0.35;
    const center = size / 2;
    const circleCenterY = center;
    const arcStartX = center - radius * 0.7;
    const arcEndX = center + radius * 0.7;
    const arcBaselineY = circleCenterY - radius * 1.2;
    const arcControlY = circleCenterY - radius * 0.9;
    return (
      <>
        <Circle
          x={center}
          y={circleCenterY}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Path
          data={`
            M ${arcStartX} ${arcBaselineY}
            Q ${center} ${arcControlY}, ${arcEndX} ${arcBaselineY}
          `}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fillEnabled={false}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'classic-female') {
    const radius = size * 0.35
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center - radius * 0.4}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 0.6,
            center,
            center + radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.0,
            center + radius * 0.4,
            center + radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'classic-male') {
    const radius = size * 0.35
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'demigender') {
    const radius = size * 0.35;
    const center = size / 2;
    const circleCenterY = center + radius * 0.25;
    const stemStartY = circleCenterY - radius;
    const stemEndY = stemStartY - radius * 0.9;
    const arrowArm = radius * 0.4;
    return (
      <>
        <Circle
          x={center}
          y={circleCenterY}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            stemStartY,
            center,
            stemEndY
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            stemEndY,
            center - arrowArm * 1.3,
            stemEndY + arrowArm * 0.9
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            stemEndY,
            center - arrowArm * 1.3,
            stemEndY - arrowArm * 0.9
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'androgyne') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.70,
            center - radius * 0.70,
            center - radius * 1.38,
            center - radius * 1.38
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.95 - radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 - radius * 0.25
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'demiboy') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'demigirl') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center - radius * 0.1}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 0.9,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.0,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'intergender') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center - radius * 0.05}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.55,
            center + radius * 0.75,
            center + radius * 1.1,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center + radius * 0.58,
            center + radius * 1.3,
            center + radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.5,
            center + radius * 1.4,
            center + radius * 0.85,
            center + radius * 1.05
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.3,
            center + radius * 1.2,
            center + radius * 1.3,
            center - radius * - 0.7
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'transgender-classic') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35,
            center - radius * 1.2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.2,
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'transgender-alt') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.70,
            center - radius * 0.70,
            center - radius * 1.38,
            center - radius * 1.38
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.95 - radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 - radius * 0.25
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  if (shapeType === 'genderqueer') {
    const radius = size * 0.35;
    const center = size / 2;
    return (
      <>
        <Circle
          x={center}
          y={center}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            center, center - radius,
            center, center - radius * 1.8
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3
          ]}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Gay - square with solid triangle indicator
  if (shapeType === 'gay') {
    return (
      <>
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.1, size * 0.1,       // top-left corner
            size * 0.9, size * 0.1,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Lesbian - circle with solid triangle indicator
  if (shapeType === 'lesbian') {
    return (
      <>
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.2, size * 0.2,       // top-left corner
            size * 0.8, size * 0.2,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </>
    );
  }

  // Bisexual female - circle with dashed triangle indicator
  if (shapeType === 'bisexual-female') {
    return (
      <>
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.2, size * 0.2,       // top-left corner
            size * 0.8, size * 0.2,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={[6, 3]} 
          dashEnabled={true}
        />
      </>
    );
  }

  // Bisexual male - square with dashed triangle indicator
  if (shapeType === 'bisexual-male') {
    return (
      <>
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        <Line
          points={[
            size * 0.2, size * 0.2,       // top-left corner
            size * 0.8, size * 0.2,       // top-right corner
            size * 0.5, size * 0.9       // bottom point
          ]}
          closed
          fillEnabled={false}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={[6, 3]} 
          dashEnabled={true}
        />
      </>
    );
  }

  // Standard male - square
  if (shapeType === 'male') {
    return (
      <Rect
        x={0}
        y={0}
        width={size}
        height={size}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    );
  }

  // Standard female - circle
  if (shapeType === 'female') {
    return (
      <Circle
        x={size/2}
        y={size/2}
        radius={size/2}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    );
  }

  // Stillbirth - shape with X mark
  if (isStillbirth) {
    const baseShape = isMale ? (
      <Rect
        x={0}
        y={0}
        width={size}
        height={size}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    ) : (
      <Circle
        x={size/2}
        y={size/2}
        radius={size/2}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={dash}
        dashEnabled={dashEnabled}
      />
    );
    return (
      <>
        {baseShape}
        <Line
          points={[size * 0.15, size * 0.15, size * 0.85, size * 0.85]}
          strokeWidth={2}
          stroke={strokeColor}
        />
        <Line
          points={[size * 0.85, size * 0.15, size * 0.15, size * 0.85]}
          strokeWidth={2}
          stroke={strokeColor}
        />
      </>
    );
  }

  // Standard male - square
  if (isMale) {
    return (
      <>
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        {isDeceased && (
          <>
            <Line
              points={[-size * 0.1, -size * 0.1, size * 1.1, size * 1.1]}
              strokeWidth={2}
              stroke={strokeColor}
            />
            <Line
              points={[size * 1.1, -size * 0.1, -size * 0.1, size * 1.1]}
              strokeWidth={2}
              stroke={strokeColor}
            />
          </>
        )}
      </>
    );
  } else if (isFemale) {
    // Standard female - circle
    return (
      <>
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        {isDeceased && (
          <>
            <Line
              points={[0, 0, size, size]}
              strokeWidth={2}
              stroke={strokeColor}
            />
            <Line
              points={[0, size, size, 0]}
              strokeWidth={2}
              stroke={strokeColor}
            />
          </>
        )}
      </>
    );
  } else {
    // Unknown gender - diamond top-left anchored
    return (
      <>
        <Line
          points={[
            size/2, 0,
            size, size/2,
            size/2, size,
            0, size/2
          ]}
          closed
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          dashEnabled={dashEnabled}
        />
        {isDeceased && (
          <>
            <Line
              points={[size*0.1, size*0.1, size*0.9, size*0.9]}
              stroke="#333"
              strokeWidth={2}
            />
            <Line
              points={[size*0.1, size*0.9, size*0.9, size*0.1]}
              stroke="#333"
              strokeWidth={2}
            />
          </>
        )}
      </>
    );
  }
};

/**
 * Helper function to determine shape properties from shapeType string
 */
export function getShapeProperties(shapeType: string) {
  // Check for transgender first to handle transgender-male/transgender-female correctly
  // Since 'female' contains 'male' as substring, we need explicit checks
  const isTransgender = shapeType.includes('transgender');
  const isTransgenderMale = shapeType === 'transgender-male' || shapeType.includes('transgender-male');
  const isTransgenderFemale = shapeType === 'transgender-female' || shapeType.includes('transgender-female');
  
  const isMale = (shapeType.includes('male') && !shapeType.includes('female')) || isTransgenderMale;
  const isFemale = (shapeType.includes('female') && !shapeType.includes('male')) || isTransgenderFemale;
  const isIndexPerson = shapeType.includes('index-person');
  const isPregnancy = shapeType.includes('pregnant') || shapeType.includes('pregnancy');
  const isDeceased = shapeType.includes('deceased');
  const isStillbirth = shapeType.includes('stillbirth');
  
  let sexualOrientation: string | undefined;
  if (shapeType.includes('gay')) sexualOrientation = 'gay';
  else if (shapeType.includes('lesbian')) sexualOrientation = 'lesbian';
  else if (shapeType.includes('bisexual')) sexualOrientation = 'bisexual';

  return {
    isMale,
    isFemale,
    isTransgender,
    isIndexPerson,
    isPregnancy,
    isDeceased,
    isStillbirth,
    sexualOrientation
  };
}
